import { api } from 'api';
import { ReaderStatusAnswer } from 'api/payment/types';

// OFFLINE READER GUARD (owner 22 Oct 2026). Before a NEW card request, ask the
// chosen reader's live status. Only a reader Clover itself reports as offline
// or with Cloud Pay Display closed is refused: a card sent there never arrives
// and the sale gets stuck (22 Oct: Westchester, Office 3).
//
// v2 (owner 22 Oct): refuse only a FRESH, Clover-confirmed answer: the server
// says stale: false (a server older than v2 sends no stale field, so nothing
// is ever refused there) and checkedAt is within 30 s. Everything uncertain
// (unknown, checking, in a sale, login, waiting, a stale or old answer, a
// failed or slow check) sends exactly as before, silently.
export const OFFLINE_READER_STATUSES = ['offline', 'app_closed'];
export const READER_CONFIRMED_MAX_AGE_MS = 30_000;

// Owner 22 Oct: the check may take about a second; past 3 s, Credit goes
// ahead as before (fail open).
export const READER_GUARD_TIMEOUT_MS = 3_000;

// A press elsewhere while Credit checks the reader cancels the request.
export const CARD_REQUEST_CANCELLED =
  'Card request cancelled. Press Credit again.';

// What happened, for the reader pop-up (owner 24 Oct). The pop-up itself says
// what to do next: the reader dropdown, or how to bring this reader back.
export const offlineReaderMessage = (
  reader: Pick<ReaderStatusAnswer, 'name' | 'status'>
) => {
  const name = reader.name || 'This reader';
  return reader.status === 'app_closed'
    ? `${name} can't take cards right now: its Clover app is closed. The card request was not sent.`
    : `${name} is offline. The card request was not sent.`;
};

export const isConfirmedOffline = (
  reader?: Pick<ReaderStatusAnswer, 'stale' | 'status' | 'checkedAt'> | null,
  now = Date.now()
) => {
  if (!reader?.status || !OFFLINE_READER_STATUSES.includes(reader.status)) {
    return false;
  }
  if (reader.stale !== false || !reader.checkedAt) {
    return false;
  }
  const checkedAt = Date.parse(reader.checkedAt);
  return (
    Number.isFinite(checkedAt) &&
    Math.abs(now - checkedAt) <= READER_CONFIRMED_MAX_AGE_MS
  );
};

export const findOfflineReader = async (
  deviceId: number
): Promise<ReaderStatusAnswer | null> => {
  if (!Number.isSafeInteger(deviceId) || deviceId <= 0) {
    return null;
  }
  const controller =
    typeof AbortController === 'undefined' ? undefined : new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const answer = await Promise.race([
      api.payment.checkReaderStatus({ deviceId, signal: controller?.signal }),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          controller?.abort();
          reject(new Error('READER_CHECK_TIMEOUT'));
        }, READER_GUARD_TIMEOUT_MS);
      }),
    ]);
    const reader = answer?.data;
    return isConfirmedOffline(reader) ? reader : null;
  } catch (error) {
    console.warn(
      '[PAYMENT] Reader status check failed; Credit continues (fail open)',
      error
    );
    return null;
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
};
